import type { IQueryParam, IListResponse } from '../api/types';
import { DocumentDataStatusFilters } from './packager-types';

export const MAT_FILTER_KEYS = [
  'DataPool',
  'CreatedAt',
  'UpdatedAt',
  'MatRowIndex',
  'MatKey',
  'SiteId',
  'c-SiteId',
  'c-SiteName',
  'VendorName',
  'std-VendorName',
  'nrm-VendorName',
  'c-VendorName',
  'VendorType',
  'AccountNumber',
  'nrm-AccountNumber',
  'std-AccountNumber',
  'c-AccountNumber',
  'Currency',
  'Commodity',
  'c-Commodity',
  'MeterNumber',
  'nrm-MeterNumber',
  'std-MeterNumber',
  'c-MeterNumber',
  'BilledUsageUnit',
  'nrm-BilledUsageUnit',
  'std-BilledUsageUnit',
  'MeterUsageUnit',
  'nrm-MeterUsageUnit',
  'std-MeterUsageUnit',
  'BilledDemandUsageUnit',
  'nrm-BilledDemandUsageUnit',
  'std-BilledDemandUsageUnit',
  'MeterDemandUsageUnit',
  'nrm-MeterDemandUsageUnit',
  'std-MeterDemandUsageUnit',
  'ServiceAddress',
  'std-ServiceAddress',
  'std-AddressCountry',
  'std-AddressState',
  'std-AddressCity',
  'std-AddressPostalCode',
  'std-AddressStreet',
  'EmissionsReportingUnits',
  'EmissionsConversionFactor',
  'DataSourceFrequency',
  'DataSourceStatus',
] as const;

export type MatFilterKey = (typeof MAT_FILTER_KEYS)[number];

export const MAT_STATUS_OPTIONS = [
  'created',
  'active',
  'inactive',
  'discovered',
  'cancelled',
] as const satisfies readonly DiscoveredDataSourceStatus[];

export const isMatFilterKey = (k: string): k is MatFilterKey =>
  (MAT_FILTER_KEYS as readonly string[]).includes(k);

export interface IMATEntry {
  VendorName: string | null;
  VendorType: string | null;
  AccountNumber: string | null;
  Commodity: string | null;
  MeterNumber: string | null;
  DataSourceFrequency: number | null;
  DataSourceStatus: DiscoveredDataSourceStatus | null;
  CreatedAt: string;
  UpdatedAt: string;
  DataPool: string | null;
  MatRowIndex: number;
  MatKey: string;
  SiteId: string | null;
  'c-SiteId': string | null;
  'c-SiteName': string | null;
  'nrm-VendorName': string | null;
  'std-VendorName': string | null;
  'c-VendorName': string | null;
  'nrm-AccountNumber': string | null;
  'std-AccountNumber': string | null;
  'c-AccountNumber': string | null;
  Currency: string | null;
  'c-Commodity': string | null;
  'nrm-MeterNumber': string | null;
  'std-MeterNumber': string | null;
  'c-MeterNumber': string | null;
  BilledUsageUnit: string | null;
  'nrm-BilledUsageUnit': string | null;
  'std-BilledUsageUnit': string | null;
  MeterUsageUnit: string | null;
  'nrm-MeterUsageUnit': string | null;
  'std-MeterUsageUnit': string | null;
  BilledDemandUsageUnit: string | null;
  'nrm-BilledDemandUsageUnit': string | null;
  'std-BilledDemandUsageUnit': string | null;
  MeterDemandUsageUnit: string | null;
  'nrm-MeterDemandUsageUnit': string | null;
  'std-MeterDemandUsageUnit': string | null;
  ServiceAddress: string | null;
  'std-ServiceAddress': string | null;
  'std-AddressCountry': string | null;
  'std-AddressState': string | null;
  'std-AddressCity': string | null;
  'std-AddressPostalCode': string | null;
  'std-AddressStreet': string | null;
  EmissionsReportingUnits: string | null;
  EmissionsConversionFactor: string | null;
  'c-Extra': Record<string, any> | null;
}
export interface IMATListParams extends IQueryParam {
  DataPool?: string;
  AccountNumber?: string;
  'nrm-AccountNumber'?: string;
  'std-AccountNumber'?: string;
  'c-AccountNumber'?: string;
  'std-VendorName'?: string;
  'nrm-VendorName'?: string;
  'c-VendorName'?: string;
  Commodity?: string;
  'c-Commodity'?: string;
  MeterNumber?: string;
  'nrm-MeterNumber'?: string;
  'std-MeterNumber'?: string;
  'c-MeterNumber'?: string;
  CreatedAt?: string; // ISO datetime string
  CreateAt?: string; // tolerate backend typo
  UpdatedAt?: string; // ISO datetime string
  DataSourceStatus?: DiscoveredDataSourceStatus;
  MatKey?: string;
  MatRowIndex?: string;
  SiteId?: string;
  'c-SiteId'?: string;
  'c-SiteName'?: string;
  VendorName?: string;
  VendorType?: string;
  Currency?: string;
  BilledUsageUnit?: string;
  'nrm-BilledUsageUnit'?: string;
  'std-BilledUsageUnit'?: string;
  MeterUsageUnit?: string;
  'nrm-MeterUsageUnit'?: string;
  'std-MeterUsageUnit'?: string;
  BilledDemandUsageUnit?: string;
  'nrm-BilledDemandUsageUnit'?: string;
  'std-BilledDemandUsageUnit'?: string;
  MeterDemandUsageUnit?: string;
  'nrm-MeterDemandUsageUnit'?: string;
  'std-MeterDemandUsageUnit'?: string;
  ServiceAddress?: string;
  'std-ServiceAddress'?: string;
  'std-AddressCountry'?: string;
  'std-AddressState'?: string;
  'std-AddressCity'?: string;
  'std-AddressPostalCode'?: string;
  'std-AddressStreet'?: string;
  EmissionsReportingUnits?: string;
  EmissionsConversionFactor?: string;
  DataSourceFrequency?: string;
}

export interface IMATListResponse extends IListResponse {
  results: IMATEntry[];
}

export type MatDiscoveryData = {
  VendorName: string | null;
  VendorType: string | null;
  AccountNumber: string | null;
  Commodity: string | null;
  MeterNumber: string | null;
  MatKey: string | null;
  'nrm-VendorName': string | null;
  'std-VendorName': string | null;
  'nrm-AccountNumber': string | null;
  'std-AccountNumber': string | null;
  Currency: string | null;
  'nrm-MeterNumber': string | null;
  'std-MeterNumber': string | null;
  BilledUsageUnit: string | null;
  'nrm-BilledUsageUnit': string | null;
  'std-BilledUsageUnit': string | null;
  MeterUsageUnit: string | null;
  'nrm-MeterUsageUnit': string | null;
  'std-MeterUsageUnit': string | null;
  BilledDemandUsageUnit: string | null;
  'nrm-BilledDemandUsageUnit': string | null;
  'std-BilledDemandUsageUnit': string | null;
  MeterDemandUsageUnit: string | null;
  'nrm-MeterDemandUsageUnit': string | null;
  'std-MeterDemandUsageUnit': string | null;
  ServiceAddress: string | null;
  'std-ServiceAddress': string | null;
  'std-AddressCountry': string | null;
  'std-AddressState': string | null;
  'std-AddressCity': string | null;
  'std-AddressPostalCode': string | null;
  'std-AddressStreet': string | null;
  EmissionsReportingUnits: string | null;
  EmissionsConversionFactor: string | null;
};

export type DiscoveredDataSourceStatus =
  | 'discovered'
  | 'active'
  | 'inactive'
  | 'created'
  | 'cancelled';
export interface MatDiscoveryRow {
  data_pool: string;
  source_extraction_batch: string;
  document_data_status: DocumentDataStatusFilters;
  data: MatDiscoveryData;
}

export interface MatDiscoveryItem {
  mat_key: string;
  mat_id: number | null;
  data_source_status: DiscoveredDataSourceStatus;
  discovered_rows: MatDiscoveryRow[];
}

export interface MatDiscoveryResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: MatDiscoveryItem[];
}

export interface MatDiscoveryKeysOnlyResponse {
  count: number;
  discovered_mat_keys: string[];
}

export interface MatFieldsInfoResponse {
  read_only_fields: string[];
  post_only_fields: string[];
  required_fields?: string[];
}
